import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ChevronRight,
  Check,
  Award,
  Clock,
  GraduationCap,
  ArrowRight,
  MessageCircle,
  Phone,
  Plus,
  Minus,
  ClipboardList,
  Scissors,
  BadgeCheck,
  BedDouble,
  PersonStanding,
  Monitor,
  Dumbbell,
  Sparkles,
} from "lucide-react";

const splitSentencesSafely = (text) => {
  const abbreviationMap = {
    "Dr.": "Dr<dot>",
    "Mr.": "Mr<dot>",
    "Mrs.": "Mrs<dot>",
    "Ms.": "Ms<dot>",
    "Rs.": "Rs<dot>",
  };

  let normalized = text;
  Object.entries(abbreviationMap).forEach(([abbr, token]) => {
    normalized = normalized.replaceAll(abbr, token);
  });

  const parts = normalized.split(/(?<=[.!?])\s+(?=[A-Z])/).filter(Boolean);
  return parts.map((part) => {
    let restored = part;
    Object.entries(abbreviationMap).forEach(([abbr, token]) => {
      restored = restored.replaceAll(token, abbr);
    });
    return restored.trim();
  });
};

const splitWhatIs = (text) => {
  const sentences = splitSentencesSafely(text);
  if (sentences.length < 2) return [text, text];
  const mid = Math.ceil(sentences.length / 2);
  const first = `${sentences.slice(0, mid).join(". ")}${sentences.slice(0, mid).join(". ").endsWith(".") ? "" : "."}`;
  const second = `${sentences.slice(mid).join(". ")}${sentences.slice(mid).join(". ").endsWith(".") ? "" : "."}`;
  return [first, second];
};

const buildStepItems = (approach, recovery) => {
  const approachSentences = splitSentencesSafely(approach);
  const recoverySentences = splitSentencesSafely(recovery);
  return [
    {
      title: "Before",
      icon: ClipboardList,
      items: [
        approachSentences[0] || "Detailed consultation and case assessment.",
        "Personalized planning based on anatomy and goals.",
        "Pre-procedure preparation and clear instructions.",
      ],
    },
    {
      title: "During",
      icon: Scissors,
      items: [
        approachSentences[1] || "Procedure performed with precision technique.",
        "Safety-focused execution in a controlled setup.",
        "Technique tailored to case complexity.",
      ],
    },
    {
      title: "After",
      icon: BadgeCheck,
      items: [
        recoverySentences[0] || "Structured post-procedure care begins.",
        recoverySentences[1] || "Follow-up and recovery monitoring as advised.",
        "Rehabilitation and review until stable outcomes.",
      ],
    },
  ];
};

const buildTimeline = (recovery) => [
  { period: "Days 1-3", description: recovery, icon: BedDouble },
  { period: "Week 1-2", description: recovery, icon: PersonStanding },
  { period: "Week 3-4", description: recovery, icon: Monitor },
  { period: "Month 2-3", description: recovery, icon: Dumbbell },
  { period: "6-12 Months", description: "Final recovery and long-term result assessment.", icon: Sparkles },
];

const ProcedureDetailPage = ({
  categoryLabel,
  categoryHref,
  title,
  whatIs,
  whyConsider,
  approach,
  recovery,
  faqs,
  related,
  ctaLine,
  heroProcedureName,
  heroIcon,
  heroImageSrc,
  heroImageAlt,
  testimonialProcedureName,
  seoDescription,
  seoCanonical,
}) => {
  const [whatIsPara1, whatIsPara2] = splitWhatIs(whatIs);
  const leftWhy = whyConsider.slice(0, Math.ceil(whyConsider.length / 2));
  const rightWhy = whyConsider.slice(Math.ceil(whyConsider.length / 2));
  const candidatePoints = whyConsider.slice(0, 5);
  const steps = buildStepItems(approach, recovery);
  const milestones = buildTimeline(recovery);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://drvickyjain.com/" },
      { "@type": "ListItem", position: 2, name: categoryLabel, item: `https://drvickyjain.com${categoryHref}` },
      { "@type": "ListItem", position: 3, name: title, item: `https://drvickyjain.com${seoCanonical}` },
    ],
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["MedicalProcedure", "SurgicalProcedure"],
        name: title,
        description: seoDescription,
        url: `https://drvickyjain.com${seoCanonical}`,
        procedureType: "Surgical",
        performer: { "@type": "Physician", name: "Dr. Vicky Jain", url: "https://drvickyjain.com/about" },
      },
      ...(faqs && faqs.length > 0 ? [{
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      }] : []),
    ],
  };

  return (
    <>
      <SEO title={title} description={seoDescription} canonical={seoCanonical} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <section className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
            <li className="flex items-center gap-2">
              <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-gold" />
              <Link to={categoryHref} className="hover:text-gold transition-colors">{categoryLabel}</Link>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-gold" />
              <span className="text-deep-navy/80 font-medium">{title}</span>
            </li>
          </ol>
        </div>
      </section>

      <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">{categoryLabel}</p>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4 sm:mb-5">{title}</h1>
              <div className="w-12 h-[2px] bg-gold mb-5" />
              <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 sm:mb-8 max-w-xl font-light">{whatIsPara1}</p>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full"><Award className="w-3.5 h-3.5 text-gold flex-shrink-0" /><span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span></div>
                <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full"><Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" /><span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span></div>
                <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full"><GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" /><span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span></div>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <ProcedurePlaceholder
                procedureName={heroProcedureName || title}
                icon={heroIcon || "✨"}
                size="hero"
                imageSrc={heroImageSrc}
                imageAlt={heroImageAlt}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
            <div>
              <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">Overview</p>
              <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What Is It?</h2>
            </div>
            <div className="space-y-5 md:space-y-6 text-[15px] sm:text-base text-deep-navy/70 leading-reading">
              <p>{whatIsPara1}</p>
              <p>{whatIsPara2}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-10 md:py-14" data-testid="why-consider-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-6 md:mb-10">
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Benefits & Outcomes</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">Why Consider This Procedure?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-5 font-semibold tracking-tight">Key Benefits</h3>
              <ul className="space-y-3.5 md:space-y-4">
                {leftWhy.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-gold" />
                    </div>
                    <span className="text-[15px] sm:text-base text-deep-navy/70 leading-reading">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-5 font-semibold tracking-tight">Expected Outcomes</h3>
              <ul className="space-y-3.5 md:space-y-4">
                {rightWhy.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-gold" />
                    </div>
                    <span className="text-[15px] sm:text-base text-deep-navy/70 leading-reading">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-10 md:py-14" data-testid="good-candidate-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-6 md:mb-10">
            <p className="text-xs font-bold tracking-[0.2em] text-gold/80 uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-warm-white">Are You a Good Candidate?</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <ul className="space-y-5 md:space-y-6">
              {candidatePoints.map((item) => (
                <li key={item} className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-none bg-gold flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-deep-navy" />
                  </div>
                  <span className="text-[15px] sm:text-base text-warm-white/90 leading-reading">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-warm-white py-10 md:py-14" data-testid="dr-jain-approach-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
              <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-5 md:mb-8">Dr. Jain&apos;s Approach</h2>
              <div className="space-y-5 md:space-y-6 text-[15px] sm:text-base text-deep-navy/70 leading-reading max-w-2xl">
                {splitSentencesSafely(approach).map((chunk, idx) => (
                  <p key={idx}>{chunk.endsWith(".") ? chunk : `${chunk}.`}</p>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
              <div className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block" style={{ background: "linear-gradient(to right, #FAFAF8 0%, rgba(250, 250, 248, 0) 15%)" }} />
              <div className="mt-4 text-center lg:text-left space-y-1"><p className="font-serif text-lg text-premium-purple font-medium">Dr. Vicky Jain</p><p className="text-sm text-deep-navy/60 leading-snug">Plastic &amp; Reconstructive Surgeon</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-10 md:py-14" data-testid="step-by-step-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-6 md:mb-10">
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">Step by Step</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div key={step.title} className="bg-white border border-[#E8E2D9] overflow-hidden">
                <div className="flex justify-center pt-6 pb-2"><div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center"><step.icon className="w-7 h-7 text-gold" /></div></div>
                <div className="bg-premium-purple px-6 py-4"><h3 className="font-serif text-lg text-warm-white text-center">{step.title}</h3></div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {step.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-[15px] sm:text-sm text-deep-navy/70 leading-reading"><span className="text-gold font-bold shrink-0">•</span><span>{item}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-xs font-bold tracking-[0.2em] text-gold/80 uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">Recovery Timeline</h2>
          </div>
          <div className="hidden md:block relative">
            <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
            <div className="grid grid-cols-5 gap-4">
              {milestones.map((milestone) => (
                <div key={milestone.period} className="relative">
                  <div className="flex justify-center mb-3"><milestone.icon className="w-6 h-6 text-deep-navy/40" /></div>
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10"><div className="w-3 h-3 rounded-full bg-warm-white" /></div>
                  <div className="text-center px-1"><h4 className="font-serif text-lg text-premium-purple mb-2 font-medium tracking-tight">{milestone.period}</h4><p className="text-sm text-deep-navy/65 leading-reading">{milestone.description}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:hidden relative pl-12">
            <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
            <div className="space-y-8">
              {milestones.map((milestone) => (
                <div key={milestone.period} className="relative">
                  <div className="absolute -left-12 top-0 flex flex-col items-center"><milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" /><div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center"><div className="w-3 h-3 rounded-full bg-warm-white" /></div></div>
                  <div className="pl-4"><h4 className="font-serif text-lg text-premium-purple mb-2 font-medium tracking-tight">{milestone.period}</h4><p className="text-sm text-deep-navy/65 leading-reading">{milestone.description}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-10 md:py-14" data-testid="risks-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-3 md:mb-4">
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Informed Consent</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">Understanding the Risks</h2>
          </div>
          <p className="text-center text-[15px] sm:text-base text-white/65 mb-8 md:mb-10 max-w-2xl mx-auto leading-reading px-2">All procedures carry some degree of risk. Your consultation includes a personalized discussion.</p>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div><h3 className="font-serif text-lg text-white mb-3 font-semibold tracking-tight">General Risks</h3><ul className="space-y-2.5"><li className="flex items-start gap-3 text-[15px] sm:text-base text-white/75 leading-reading"><span className="text-gold shrink-0">—</span><span>Bleeding or infection</span></li><li className="flex items-start gap-3 text-[15px] sm:text-base text-white/75 leading-reading"><span className="text-gold shrink-0">—</span><span>Swelling and bruising</span></li><li className="flex items-start gap-3 text-[15px] sm:text-base text-white/75 leading-reading"><span className="text-gold shrink-0">—</span><span>Temporary discomfort</span></li></ul></div>
              <div><h3 className="font-serif text-lg text-white mb-3 font-semibold tracking-tight">Procedure-Specific Risks</h3><ul className="space-y-2.5"><li className="flex items-start gap-3 text-[15px] sm:text-base text-white/75 leading-reading"><span className="text-gold shrink-0">—</span><span>Variable healing response</span></li><li className="flex items-start gap-3 text-[15px] sm:text-base text-white/75 leading-reading"><span className="text-gold shrink-0">—</span><span>Need for follow-up sessions</span></li><li className="flex items-start gap-3 text-[15px] sm:text-base text-white/75 leading-reading"><span className="text-gold shrink-0">—</span><span>Rare revision requirement</span></li></ul></div>
            </div>
          </div>
        </div>
      </section>

      <PatientTestimonials procedureName={testimonialProcedureName || title} />

      <section className="bg-warm-white py-10 md:py-14" data-testid="faq-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6 md:mb-10 px-2">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto text-left">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                  <AccordionTrigger className="text-left font-serif text-base sm:text-lg text-premium-purple font-medium leading-snug hover:no-underline hover:text-gold py-5 sm:py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                    <span className="pr-4">{faq.question}</span>
                    <div className="flex-shrink-0">
                      <Plus className="w-5 h-5 text-gold" />
                      <Minus className="w-5 h-5 text-gold hidden" />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-deep-navy/70 pb-6 text-[15px] sm:text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-3 sm:mb-4 px-2">Book Your Consultation</h2>
          <p className="text-warm-white/75 mb-8 max-w-2xl mx-auto leading-reading text-[15px] sm:text-base px-4">{ctaLine}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 flex-wrap">
            <Button asChild className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base" data-testid="cta-book-consultation"><a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">Book Consultation<ArrowRight className="w-5 h-5 ml-2" /></a></Button>
            <Button asChild variant="outline" className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base" data-testid="cta-call"><a href={TEL_URL}><Phone className="w-5 h-5 mr-2" />Call {PHONE_DISPLAY}</a></Button>
          </div>
          <p className="text-warm-white/55 text-sm leading-snug px-2">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:text-warm-white/70 transition-colors">WhatsApp</a>
            {" · "}Clinic Hours: Monday – Saturday, 10:00 AM – 7:00 PM
          </p>
        </div>
      </section>

      <section className="bg-warm-white py-10 md:py-14" data-testid="related-procedures-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6 md:mb-10 text-center">Related Procedures</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((item) => (
              <div key={item.name} className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group">
                <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{item.name}</h3>
                <p className="text-deep-navy/65 mb-6 leading-reading text-[15px] sm:text-base">Learn more about this related treatment pathway.</p>
                <Link to={item.href} className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors">Learn More →<ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProcedureDetailPage;
